<script setup lang="ts">
import { computed } from 'vue';
import type { Component } from 'vue';

import { switcher } from '@/common/switcher';
import { ItemType } from '@/library/message/lib';
import type { MessageListItem } from '@/library/message/types';

// These components are currently loaded all at once, we should
// add some logic to load them async and prefetch based on the requirements.
import TypingMessageOwn from './messages/TypingMessageOwn.vue';
import SystemListMessage from './messages/SystemListMessage.vue';
import ChatMessageWrapper from './messages/ChatMessageWrapper.vue';
import ChatNotice from './notices/ChatNotice.vue';
import MediaMessage from './messages/MediaMessage.vue';
import ResolutionMessage from './messages/ResolutionMessage/ResolutionMessage.vue';
import AlternativeActionsMessage from './messages/AlternativeActionsMessage.vue';
import PreSupportConfirmation from './messages/PreSupportConfirmation.vue';
import PreSupportPromptMessage from './messages/PreSupportPromptMessage.vue';
import PreSupportPredictionMessage from './messages/PreSupportPredictionMessage.vue';
import ArticleListWithOtherNavigationMessage from './messages/ArticleListWithOtherNavigationMessage.vue';
import SelectTransactionMessage from './messages/TransactionMessage/SelectTransactionMessage.vue';
import SelectedTransactionMessage from './messages/TransactionMessage/SelectedTransactionMessage.vue';
import ConnectOptionsMessage from './messages/ConnectOptionsMessage/ConnectOptionsMessage.vue';

interface ChatItemProps {
  item?: MessageListItem;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<ChatItemProps>();

const messageSwitch = switcher()
  .when(ItemType.isTypingMessage, () => TypingMessageOwn)
  .when(ItemType.isListMessage, () => SystemListMessage)
  .when(ItemType.isMediaMessage, () => MediaMessage)
  .when(ItemType.isResolutionMessage, () => ResolutionMessage)
  .when(ItemType.isArticleListWithOtherNavigation, () => ArticleListWithOtherNavigationMessage)
  .when(ItemType.isAlternativeActionsMessage, () => AlternativeActionsMessage)
  .when(ItemType.isPreSupportPromptConfirmationMessage, () => PreSupportConfirmation)
  .when(ItemType.isPreSupportPromptMessage, () => PreSupportPromptMessage)
  .when(ItemType.isPreSupportPredictionMessage, () => PreSupportPredictionMessage)
  .when(ItemType.isTransactionSelectMessage, () => SelectTransactionMessage)
  .when(ItemType.isSelectedTransactionMessage, () => SelectedTransactionMessage)
  .when(ItemType.isConnectOptionSelectMessage, () => ConnectOptionsMessage)
  .none(() => ChatMessageWrapper);

const MessageComponent = computed(() => messageSwitch.match<Component>(props.item));
</script>

<template>
  <ChatNotice v-if="ItemType.isNotice(item)" :notice="item" v-on="$listeners" />
  <component :is="MessageComponent" v-else :message="item" :next="next" :previous="previous" v-on="$listeners" />
</template>
